/**
 * Runtime configuration (research.md §13). The container writes `/config.js`
 * from its environment at start-up, so one image serves every deployment; it
 * assigns `globalThis.__KINDRA_CONFIG__`, which pages and the push service
 * worker (no `window` there) can both read.
 */
export type CallProviderName = 'zego' | 'fake';

export interface FirebaseWebConfig {
  apiKey: string;
  authDomain?: string;
  projectId: string;
  messagingSenderId: string;
  appId: string;
}

export interface KindraConfig {
  /** The API origin, e.g. https://kindra.codetogod.com (no trailing slash). */
  apiUrl: string;
  /** This web app's own origin, used for shareable links such as /connect/<code>. */
  webUrl?: string;
  zegoAppId?: number;
  firebase?: FirebaseWebConfig;
  /** Public VAPID key for browser push (Firebase Cloud Messaging). */
  vapidKey?: string;
  callProvider: CallProviderName;
}

export class ConfigError extends Error {
  constructor(message: string) {
    super(`Kindra is misconfigured: ${message}`);
    this.name = 'ConfigError';
  }
}

const CALL_PROVIDERS: readonly CallProviderName[] = ['zego', 'fake'];
const stripSlash = (url: string) => url.replace(/\/+$/, '');

export function getConfig(): KindraConfig {
  const raw = (globalThis as { __KINDRA_CONFIG__?: Partial<KindraConfig> }).__KINDRA_CONFIG__;
  if (!raw || typeof raw !== 'object') {
    throw new ConfigError('/config.js did not load (globalThis.__KINDRA_CONFIG__ is missing).');
  }
  if (typeof raw.apiUrl !== 'string' || !raw.apiUrl) {
    throw new ConfigError('apiUrl is missing from /config.js.');
  }
  const callProvider = raw.callProvider ?? 'zego';
  if (!CALL_PROVIDERS.includes(callProvider)) {
    throw new ConfigError(`callProvider must be one of ${CALL_PROVIDERS.join(', ')}.`);
  }
  const config: KindraConfig = { apiUrl: stripSlash(raw.apiUrl), callProvider };
  if (raw.webUrl) config.webUrl = stripSlash(raw.webUrl);
  if (raw.zegoAppId !== undefined) config.zegoAppId = Number(raw.zegoAppId);
  if (raw.firebase) config.firebase = raw.firebase;
  if (raw.vapidKey) config.vapidKey = raw.vapidKey;
  return config;
}
